/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Explore (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import StringResources from './nls/StringResources';
import SlotEditorSlot from './SlotEditorSlot';
import SlotEditorA11YHandler from './SlotEditorA11YHandler';
import { Accordion, AccordionItem } from '@ba/ba-ui-carbon-toolkit';
import SlotEditorEmptyState from './SlotEditorEmptyState';
import { isAlive } from 'mobx-state-tree';
import ScrollableDragHandler from './ScrollableDragHandler';
import '../stylesheets/slotEditor.scss';

/**
 * The SlotEditor View
 */
class SlotEditorView extends Component {
	constructor(props) {
		super(props);
		//Handle slot-editor-specific accessibility actions (eg swapping slot items etc.)
		this.a11YHandler = new SlotEditorA11YHandler();
		this.state = {
			needsRefresh: null
		};
		this.slotEditorRef = React.createRef();
	}

	componentDidMount() {
		this._initializeScrollDragSupport();
	}

	componentWillUnmount(){
		this.dropTarget && this.dropTarget.remove();
	}
	render() {
		return <div
			ref={this.slotEditorRef}
			className="slotEditor"
		>
			{this.renderContent()}
		</div>;
	}

	renderContent() {
		const { slotEditorModel } = this.props;
		if (!slotEditorModel || !isAlive(slotEditorModel)) {
			// unable to render the view if the model is dead
			return null;
		}
		const selectedContent = slotEditorModel.selectedContents[0];
		const SLOT_EDITOR_STATES = slotEditorModel.SLOT_EDITOR_STATES;

		const currentState = slotEditorModel.slotEditorState;
		if (currentState === SLOT_EDITOR_STATES.DISPLAY) {

			if(!this._canRenderSlotEditor(selectedContent)) {
				return this._renderEmptyState(SLOT_EDITOR_STATES, SLOT_EDITOR_STATES.EMPTY.NO_VIS_CREATED);
			}

			const result = this.renderSlotsAndExtensions();
			if (result) {
				return result;
			} else {
				return this._renderEmptyState(SLOT_EDITOR_STATES);
			}
		} else {
			return this._renderEmptyState(SLOT_EDITOR_STATES, currentState);
		}
	}

	_getSlotsSection() {
		const { slotEditorModel } = this.props;
		const slotInfo = slotEditorModel.getSlotInfo();
		return (
			<div className="slotEditor__slots">
				{(slotInfo.length > 1) ? this._renderLayersAndSlots(slotInfo) : this._renderSlots(slotInfo[0].slotAPIs)}
			</div>
		);
	}


	renderSlotsAndExtensions() {
		const { slotEditorModel } = this.props;
		const slotInfo = slotEditorModel.getSlotInfo();
		let sections = [
			{
				index: 0,
				render: this._getSlotsSection.bind(this)
			}
		];

		if(this.props.extensions !== undefined) {
			sections = sections.concat(this.props.extensions);
		}
		const sorted_sections = sections.sort((a, b) => a.index - b.index);

		if(slotInfo.length > 0) {
			return (
				<React.Fragment>
					{ sorted_sections.map((section) => section.render()) }
				</React.Fragment>
			);
		}
	}

	_initializeScrollDragSupport = () => {
		const { dndManager: dndManagerPromise } = this.props;
		dndManagerPromise && dndManagerPromise.then(
			(dndManager) => {
				if(this.slotEditorRef.current) {
					this.scrollableDragHandler = new ScrollableDragHandler({
						scrollableContainer: this.slotEditorRef.current,
						dndManager
					});
					this.dropTarget = dndManager.addDropTarget(this.slotEditorRef.current, this.scrollableDragHandler);
				}
			}
		);
	}

	_canRenderSlotEditor(content) {
		// in some edge cases, there is no `visualization` in `content`, we cannot display slot editor without `visualization`
		return !!(content && content.getFeature('Visualization'));
	}

	_renderEmptyState(SLOT_EDITOR_STATES, currentSlotEditorState) {
		switch (currentSlotEditorState) {
			case SLOT_EDITOR_STATES.EMPTY.NO_VIS_CREATED:
				return this._getEmptyState(StringResources.get('slotEditor_emptyState_vis_not_created_title'), StringResources.get('slotEditor_emptyState_vis_not_created_description'));
			case SLOT_EDITOR_STATES.EMPTY.NOT_SUPPORT_SLOTS:
				return this._getEmptyState(StringResources.get('slotEditor_emptyState_widget_not_support_slot_selected_title'), StringResources.get('slotEditor_emptyState_widget_not_support_slot_selected_description'));
			case SLOT_EDITOR_STATES.EMPTY.NO_VIS_SELECTED:
				return this._getEmptyState(StringResources.get('slotEditor_emptyState_no_visualization_selected_title'), StringResources.get('slotEditor_emptyState_no_visualization_selected_description'));
			case SLOT_EDITOR_STATES.EMPTY.MUILTI_SELECTION:
				return this._getEmptyState(StringResources.get('slotEditor_emptyState_multiple_visualizations_selected_title'), StringResources.get('slotEditor_emptyState_multiple_visualizations_selected_description'));
			case SLOT_EDITOR_STATES.EMPTY.ERROR:
				return this._getEmptyState(StringResources.get('slotEditor_emptyState_visualization_has_error_title'), null , 'error');
			default:
				return this._getEmptyState();
		}
	}

	_getEmptyState(title, description, type) {
		return (
			<SlotEditorEmptyState
				title={title}
				description={description}
				type={type}
			/>
		);
	}

	/**
	 * For the multi-layer case, we need to draw the layer headings and the slots per layer.
	 * @param slotInfoByLayer - a structure containing the layerId and the array of slotAPI's assigned to that layer.
	 */
	_renderLayersAndSlots(slotInfoByLayer) {
		return slotInfoByLayer.map((slotInfoByLayerEntry) => {
			if (!slotInfoByLayerEntry.isHideLayer) {
				return <Accordion key={slotInfoByLayerEntry.layerId}>
					<AccordionItem
						icon="right"
						itemName={slotInfoByLayerEntry.layerCaption}
						open={slotInfoByLayerEntry.open}
						fullWidth={true}
					>
						{this._renderSlots(slotInfoByLayerEntry.slotAPIs, slotInfoByLayerEntry.layerId, '__multilayer' /*customeClass*/)}
					</AccordionItem>
				</Accordion>;
			} else {
				return this._renderSlots(slotInfoByLayerEntry.slotAPIs);
			}
		});
	}

	/**
	 * Called to render the data slots (or a single layer worth of slots for the multi-layer scenario).
	 * @param slotAPIs - the slots to render
	 * @param pLayerId - (optional) the layerId to render
	 * @param className - (optional) the custom class name to render
	 */
	_renderSlots(slotAPIs, pLayerId, className) {
		var layerId = pLayerId || '';
		return slotAPIs.map((slotAPI) => {
			return (slotAPI.getDefinition().isHidden()) ?
				[] :
				<SlotEditorSlot
					className={className}
					key={layerId + slotAPI.getDefinition().getId()}
					slotAPI={slotAPI}
					dataItemAPIs={slotAPI.getDataItemList()}
					a11Y={this.a11YHandler}
					slotDataItemHandler={this.props.slotEditorModel.getSlotDataItemHandler(slotAPI, -1)}
					slotEditorModel={this.props.slotEditorModel}
					dndManager={this.props.dndManager}
				/>;
		});
	}
}

SlotEditorView.propTypes = {
	dndManager: PropTypes.object.isRequired,
	slotEditorModel: PropTypes.object.isRequired,
	extensions: PropTypes.array
};

export default observer(SlotEditorView);



// WEBPACK FOOTER //
// ./src/js/SlotEditorView.js